import { useEffect, useMemo, useState } from 'react'
import { Chip, Typography } from '@mui/material'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../loading-page'
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display'
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons'
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import SuperAdminService from '../../../shared/services/super-admin/super-admin.service'
import { ISupplierMatchOverride } from '../../../shared/services/super-admin/super-admin.model'
import { AddEditSupplierMatchDialog } from '../../dialogs/super-admin/add-edit-supplier-match-dialog'

export const SuperAdminSupplierMatchesPage = () => {
  const superAdminService = new SuperAdminService()

  const [overrides, setOverrides] = useState<ISupplierMatchOverride[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [editing, setEditing] = useState<ISupplierMatchOverride | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ISupplierMatchOverride | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  // Table state
  const [selected, setSelected] = useState<string[]>([])
  const [inputPage, setInputPage] = useState(0)
  const [perPage, setPerPage] = useState(25)

  const load = () => {
    setIsLoading(true)
    superAdminService.getSupplierMatchOverrides()
      .then(response => setOverrides(response))
      .catch(err => console.log(err))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => { load() }, [])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return overrides
    return overrides.filter(o =>
      o.alias.toLowerCase().includes(q) || (o.companyName || '').toLowerCase().includes(q))
  }, [overrides, searchQuery])

  // TableDisplay paginates server-style (it renders whatever rows it is given as the current page),
  // so slice to the current page here. Reset to page 0 whenever the filter changes.
  useEffect(() => { setInputPage(0) }, [searchQuery])
  const paged = useMemo(
    () => filtered.slice(inputPage * perPage, inputPage * perPage + perPage),
    [filtered, inputPage, perPage])

  const addHandler = () => { setEditing(null); setOpenDialog(true) }
  const editHandler = (override: ISupplierMatchOverride) => { setEditing(override); setOpenDialog(true) }
  const deleteHandler = (override: ISupplierMatchOverride) => setDeleteTarget(override)
  const confirmDelete = () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    superAdminService.deleteSupplierMatchOverride(deleteTarget.id)
      .then(() => { setDeleteTarget(null); load() })
      .catch(err => console.log(err))
      .finally(() => setIsDeleting(false))
  }

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false },
    { id: 'alias', numeric: false, label: 'Alias', sortable: false, isHeading: true },
    { id: 'company', numeric: false, label: 'Companies House company', sortable: false },
    { id: 'sicCode', numeric: false, label: 'SIC', sortable: false },
    { id: 'status', numeric: false, label: 'Status', sortable: false },
  ]

  const rows = paged.map(o => ({
    id: o.id,
    alias: o.alias,
    company: `${o.companyName || ''}${o.companyNumber ? ` (${o.companyNumber})` : ''}`,
    sicCode: o.sicCode || '',
    status: o.active
      ? <Chip size="small" color="success" variant="outlined" label="Active" />
      : <Chip size="small" variant="outlined" label="Inactive" />,
    action: (
      <TableActionButtons
        id={o.id}
        includeEdit={true}
        includeDelete={true}
        onEdit={() => editHandler(o)}
        onDelete={() => deleteHandler(o)}
      />
    ),
  }))

  return (
    <PageOuterContainer>
      <PageHeading
        title="Supplier matches"
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={addHandler}
        actionButtonText="Add match"
      />
      <div style={{ maxWidth: 360, marginBottom: 16 }}>
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      </div>
      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={filtered.length}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>
      <AddEditSupplierMatchDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        override={editing}
        onSaved={load}
      />
      <DialogWrapper
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete supplier match"
        cancelHandler={() => setDeleteTarget(null)}
        submitHandler={confirmDelete}
        isSubmitting={isDeleting}
        maxWidth="xs"
        submitButtonText="Delete"
        submitButtonLoadingText="Deleting"
      >
        <Typography variant="body1">
          Delete the override for "{deleteTarget?.alias}"? This cannot be undone.
        </Typography>
      </DialogWrapper>
    </PageOuterContainer>
  )
}
